import { useQuery as useReactQuery } from "@tanstack/react-query";
import destr from "destr";
import { useSearchParams } from "next/navigation";
import { ofetch } from "ofetch";
import { useEffect, useRef } from "react";
import { useRouter } from "@/lib/locale/navigation";
import { getFreshAuthorizationHeader } from "@/lib/nhost/access-token";
import { useOrg } from "@/lib/user-store";
import { isLandingJobId } from "./landing-upload-claim";
import type { LandingOwnershipPersistence } from "./landing-upload-lifecycle";

const localStorageKey = "landingJobIds";

export const setLocalStorageJobId = ({ jobId }: { jobId: string }): LandingOwnershipPersistence => {
  try {
    const jobIds = getLocalStorageJobIds();
    localStorage.setItem(localStorageKey, JSON.stringify(Array.from(new Set([...jobIds, jobId]))));
    return "local_storage";
  } catch {
    return "claim_link";
  }
};

export const getLocalStorageJobIds = (): string[] => {
  if (typeof window === "undefined") return [];
  try {
    const value: unknown = JSON.parse(localStorage.getItem(localStorageKey) || "[]");
    return Array.isArray(value) ? value.filter(isLandingJobId) : [];
  } catch {
    return [];
  }
};

const removeLocalStorageJobId = (jobId: string) => {
  try {
    const remainingJobIds = getLocalStorageJobIds().filter((storedJobId) => storedJobId !== jobId);
    if (remainingJobIds.length === 0) {
      localStorage.removeItem(localStorageKey);
    } else {
      localStorage.setItem(localStorageKey, JSON.stringify(remainingJobIds));
    }
  } catch {
    // The query-string claim path remains sufficient when storage is blocked.
  }
};

export const useTransferLocalStorageJobIds = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { org } = useOrg();
  const claimingJobRef = useRef<string | null>(null);

  const queryJobId = searchParams.get("landingJobId");
  const jobId = (isLandingJobId(queryJobId) ? queryJobId : undefined) ?? getLocalStorageJobIds()[0];

  const { data: localStorageJobResponse } = useReactQuery({
    queryKey: ["landing-job", jobId],
    enabled: !!jobId,
    queryFn: async () => {
      return ofetch<{
        data?: GQL_GET_LANDING_JOB_QUERY;
        error?: unknown;
      }>(`/api/v1/landing-upload/${jobId}/`, { parseResponse: destr });
    },
  });

  useEffect(() => {
    const transcriptionJob = localStorageJobResponse?.data?.transcription_jobs_by_pk;
    if (!jobId || !transcriptionJob || !org?.id || claimingJobRef.current === jobId) return;

    if (org.id === transcriptionJob.org) {
      removeLocalStorageJobId(jobId);
      router.replace(`/app/transcriptions/${jobId}`);
      return;
    }

    claimingJobRef.current = jobId;
    void getFreshAuthorizationHeader()
      .then((authHeader) =>
        ofetch(`/api/v1/landing-upload/${jobId}/owner/`, {
          method: "PUT",
          headers: authHeader,
        })
      )
      .then(() => {
        removeLocalStorageJobId(jobId);
        router.replace(`/app/transcriptions/${jobId}`);
      })
      .catch((error) => {
        claimingJobRef.current = null;
        console.error("Failed to transfer job:", error);
      });
  }, [jobId, localStorageJobResponse, org?.id, router]);
};
